import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getImageUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  useTheme,
  alpha,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  TextField,
  InputAdornment,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  Rating,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Store as StoreIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ZoomIn as ZoomInIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  AccountCircle as AccountCircleIcon,
  Wallpaper as WallpaperIcon,
} from '@mui/icons-material';
import {
  getVendorById,
  getVendorProfile,
  getVendorAttendedEvents,
  uploadGalleryImage,
  deleteGalleryImage,
  updateVendorSocialMedia,
  updateVendorProfile,
  getInstagramAuthorizeUrl,
  getInstagramProfile,
  getInstagramMedia,
  disconnectInstagram,
} from '../services/vendorService';

const VendorProfile = () => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { vendorId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [uploadProgress, setUploadProgress] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [socialMediaDialogOpen, setSocialMediaDialogOpen] = useState(false);
  const [socialMediaLinks, setSocialMediaLinks] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
  });
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const profileInputRef = useRef(null);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [aboutText, setAboutText] = useState('');
  const [instagramMedia, setInstagramMedia] = useState([]);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Fetch vendor profile and attended events
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery(
    ['vendorProfile', vendorId || 'current'],
    () => {
      console.log('VendorProfile - vendorId:', vendorId);
      if (vendorId) {
        return getVendorById(vendorId);
      } else {
        return getVendorProfile();
      }
    },
    {
      retry: 1,
      onError: (error) => {
        console.error('VendorProfile - Query error:', error);
        if (error.response?.status === 404) {
          setSnackbar({
            open: true,
            message: 'Vendor profile not found',
            severity: 'error',
          });
          if (isProfileOwner()) {
            history.push('/create-vendor-profile');
          }
        } else if (error.response?.status === 401) {
          setSnackbar({
            open: true,
            message: 'Please log in to view this profile',
            severity: 'error',
          });
          history.push('/login');
        } else {
          setSnackbar({
            open: true,
            message: 'Error loading profile',
            severity: 'error',
          });
        }
      },
      onSuccess: (data) => {
        console.log('VendorProfile - Query success:', data);
      }
    }
  );

  const { data: attendedEvents, isLoading: attendedEventsLoading } = useQuery(
    ['vendorAttendedEvents', vendorId],
    () => getVendorAttendedEvents(vendorId),
    {
      enabled: !!profile,
    }
  );

  // Upload image mutation
  const uploadImageMutation = useMutation(
    (data) => uploadGalleryImage(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setUploadDialogOpen(false);
        setSelectedImage(null);
        setImageCaption('');
        setUploadProgress(false);
        setSnackbar({
          open: true,
          message: 'Image uploaded successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setUploadProgress(false);
        setSnackbar({
          open: true,
          message: `Error uploading image: ${error.response?.data?.detail || error.message || 'Failed to upload image'}`,
          severity: 'error',
        });
      },
    }
  );

  // Delete image mutation
  const deleteImageMutation = useMutation(
    (imageId) => deleteGalleryImage(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setSnackbar({
          open: true,
          message: 'Image deleted successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error deleting image: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  // Update social media mutation
  const updateSocialMediaMutation = useMutation(
    (data) => updateVendorSocialMedia(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setSocialMediaDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'Social media links updated successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error updating social media: ${error.response?.data?.detail || error.message || 'Failed to update social media links'}`,
          severity: 'error',
        });
      },
    }
  );

  // Add update about mutation
  const updateAboutMutation = useMutation(
    (data) => updateVendorProfile(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setAboutDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'About section updated successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error updating about section: ${error.response?.data?.detail || error.message || 'Failed to update about section'}`,
          severity: 'error',
        });
      },
    }
  );

  // Instagram mutations
  const disconnectInstagramMutation = useMutation(
    () => disconnectInstagram(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setSnackbar({
          open: true,
          message: 'Instagram account disconnected successfully!',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error disconnecting Instagram: ${error.response?.data?.detail || error.message}`,
          severity: 'error',
        });
      },
    }
  );

  // Handle Instagram OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const instagramConnected = params.get('instagram_connected');
    const instagramError = params.get('instagram_error');

    if (instagramConnected === 'true') {
      setSnackbar({
        open: true,
        message: 'Instagram account connected successfully!',
        severity: 'success',
      });
      queryClient.invalidateQueries(['vendorProfile']);
      queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
      // Clean up URL
      history.replace(location.pathname);
    } else if (instagramError) {
      setSnackbar({
        open: true,
        message: `Instagram connection error: ${decodeURIComponent(instagramError)}`,
        severity: 'error',
      });
      // Clean up URL
      history.replace(location.pathname);
    }
  }, [location.search, history, location.pathname, queryClient, vendorId]);

  // Fetch Instagram media when Instagram tab is active and connected
  useEffect(() => {
    const currentUserId = localStorage.getItem('user_id');
    const isOwner = profile?.user_id === parseInt(currentUserId);
    
    if (activeTab === 4 && profile?.instagram_connected && isOwner) {
      setInstagramLoading(true);
      getInstagramMedia(12)
        .then((data) => {
          setInstagramMedia(data.media || []);
        })
        .catch((error) => {
          console.error('Error fetching Instagram media:', error);
          setSnackbar({
            open: true,
            message: 'Error loading Instagram posts',
            severity: 'error',
          });
        })
        .finally(() => {
          setInstagramLoading(false);
        });
    }
  }, [activeTab, profile?.instagram_connected, profile?.user_id]);

  const handleConnectInstagram = async () => {
    try {
      const data = await getInstagramAuthorizeUrl();
      console.log('Instagram authorization URL:', data.authorization_url);
      window.location.href = data.authorization_url;
    } catch (error) {
      console.error('Error getting Instagram authorization URL:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to connect Instagram';
      setSnackbar({
        open: true,
        message: `Error connecting Instagram: ${errorMessage}`,
        severity: 'error',
      });
    }
  };

  const handleDisconnectInstagram = () => {
    if (window.confirm('Are you sure you want to disconnect your Instagram account?')) {
      disconnectInstagramMutation.mutate();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditProfile = () => {
    history.push('/edit-vendor-profile');
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setUploadDialogOpen(true);
    }
  };

  const handleCoverPhotoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setUploadProgress(true);
        const formData = new FormData();
        formData.append('cover_photo', file);
        await updateVendorProfile(formData);
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setSnackbar({
          open: true,
          message: 'Cover photo updated successfully!',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error uploading cover photo: ${error.response?.data?.detail || error.message || 'Failed to upload cover photo'}`,
          severity: 'error',
        });
      } finally {
        setUploadProgress(false);
        // Reset the input
        if (event.target) {
          event.target.value = '';
        }
      }
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setUploadProgress(true);
        const formData = new FormData();
        formData.append('profile_picture', file);
        await updateVendorProfile(formData);
        queryClient.invalidateQueries(['vendorProfile']);
        queryClient.invalidateQueries(['vendorProfile', vendorId || 'current']);
        setSnackbar({
          open: true,
          message: 'Profile picture updated successfully!',
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error uploading profile picture: ${error.response?.data?.detail || error.message || 'Failed to upload profile picture'}`,
          severity: 'error',
        });
      } finally {
        setUploadProgress(false);
        // Reset the input
        if (event.target) {
          event.target.value = '';
        }
      }
    }
  };

  const handleUploadConfirm = () => {
    if (selectedImage) {
      setUploadProgress(true);
      uploadImageMutation.mutate({
        image: selectedImage,
        caption: imageCaption,
      });
    }
  };

  const handleDeleteImage = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSocialMediaEdit = () => {
    setSocialMediaLinks({
      facebook: profile?.social_media?.facebook || '',
      twitter: profile?.social_media?.twitter || '',
      instagram: profile?.social_media?.instagram || '',
      linkedin: profile?.social_media?.linkedin || '',
    });
    setSocialMediaDialogOpen(true);
  };

  const handleSocialMediaChange = (platform) => (event) => {
    setSocialMediaLinks({
      ...socialMediaLinks,
      [platform]: event.target.value,
    });
  };

  const handleSocialMediaSave = () => {
    // Format URLs to ensure they have http:// or https:// prefix
    const formattedLinks = {};
    Object.keys(socialMediaLinks).forEach(platform => {
      const url = socialMediaLinks[platform];
      if (url && url.trim()) {
        // If URL doesn't start with http:// or https://, add https://
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          formattedLinks[platform] = `https://${url.trim()}`;
        } else {
          formattedLinks[platform] = url.trim();
        }
      } else {
        formattedLinks[platform] = '';
      }
    });
    updateSocialMediaMutation.mutate(formattedLinks);
  };

  const isProfileOwner = () => {
    const currentUserId = localStorage.getItem('user_id');
    return profile?.user_id === parseInt(currentUserId);
  };

  const handleAboutEdit = () => {
    setAboutText(profile?.bio || profile?.about || '');
    setAboutDialogOpen(true);
  };

  const handleAboutSave = () => {
    updateAboutMutation.mutate({ bio: aboutText });
  };

  if (profileLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading vendor profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (profileError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {profileError.response?.status === 404
            ? 'Profile not found'
            : 'Error loading profile'}
        </Alert>
        {isProfileOwner() && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push('/create-vendor-profile')}
            >
              Create Profile
            </Button>
          </Box>
        )}
      </Container>
    );
  }

  const stats = {
    totalApplications: profile?.total_applications || 0,
    attendedEvents: attendedEvents?.length || 0,
    averageRating: profile?.average_rating || 0,
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      pb: 4,
      background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.grey[50], 0.8)} 30%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
    }}>
      <Container maxWidth="lg" sx={{ pt: 0 }}>
        {/* Cover Photo Section - LinkedIn/Facebook Style */}
        <Paper 
          elevation={0}
          sx={{
            position: 'relative',
            mb: 3,
            overflow: 'hidden',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 4,
          }}
        >
          <Box
            sx={{ 
              height: { xs: 200, sm: 250, md: 300 },
              position: 'relative',
              background: profile?.cover_photo_url 
                ? 'none'
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            {profile?.cover_photo_url && (
              <img
                src={getImageUrl(profile.cover_photo_url)}
                alt="Cover"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            {isProfileOwner() && (
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                }}
                onClick={() => coverInputRef.current?.click()}
              >
                <WallpaperIcon />
              </IconButton>
            )}
          </Box>

          {/* Profile Picture and Info Section - Connected */}
          <Box 
            sx={{ 
              position: 'relative', 
              px: { xs: 2, sm: 4, md: 6 }, 
              pb: 4, 
              pt: { xs: 2, sm: 3 },
              background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.grey[50], 0.5)} 50%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
              borderRadius: '0 0 8px 8px',
            }}
          >
            {/* Profile Picture - Large, LinkedIn Style */}
            <Box 
              sx={{ 
                position: 'relative', 
                display: 'inline-block', 
                mb: 3,
                mt: { xs: -8, sm: -10, md: -12 }, // Negative margin to overlap with cover photo
              }}
            >
              <Avatar
                src={profile?.profile_picture_url ? getImageUrl(profile.profile_picture_url) : undefined}
                sx={{
                  width: { xs: 120, sm: 150, md: 180 },
                  height: { xs: 120, sm: 150, md: 180 },
                  border: 5,
                  borderColor: 'background.paper',
                  bgcolor: theme.palette.primary.main,
                  boxShadow: theme.shadows[12],
                }}
              >
                {!profile?.profile_picture_url && (
                  <BusinessIcon sx={{ fontSize: { xs: 60, sm: 75, md: 90 } }} />
                )}
              </Avatar>
              {/* Decorative accent behind avatar */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 140, sm: 170, md: 200 },
                  height: { xs: 140, sm: 170, md: 200 },
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  zIndex: -1,
                }}
              />
              {isProfileOwner() && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'primary.main',
                    color: 'white',
                    border: 3,
                    borderColor: 'background.paper',
                    '&:hover': { bgcolor: 'primary.dark' },
                    width: 40,
                    height: 40,
                  }}
                  onClick={() => profileInputRef.current?.click()}
                >
                  <AccountCircleIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Main Info Section */}
            <Grid container spacing={3} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                      {profile?.business_name || 'Business Name'}
                    </Typography>
                    {profile?.verified && (
                      <Tooltip title="Verified Business">
                        <VerifiedIcon color="primary" sx={{ fontSize: 28 }} />
                      </Tooltip>
                    )}
                  </Box>

                  {profile?.category_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CategoryIcon color="primary" fontSize="small" />
                      <Chip
                        label={profile.category_name}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}

                  {/* Bio/Description */}
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.7, fontSize: '1.05rem' }}
                  >
                    {profile?.bio || 'No description available. Add a bio to tell visitors about your business.'}
                  </Typography>

                  {/* Contact Info Chips */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    {profile?.contact_info && (
                      <Chip
                        icon={<PhoneIcon />}
                        label={profile.contact_info}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                      />
                    )}
                    {profile?.email && (
                      <Chip
                        icon={<EmailIcon />}
                        label={profile.email}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                      />
                    )}
                    {profile?.location && (
                      <Chip
                        icon={<LocationIcon />}
                        label={profile.location}
                        sx={{ 
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        }}
                      />
                    )}
                  </Box>

                  {/* Social Media Links - Prominent Display with Text */}
                  {profile?.social_media && Object.keys(profile.social_media).some(key => profile.social_media[key]) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                        Connect With Us
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {profile.social_media.facebook && profile.social_media.facebook.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<FacebookIcon />}
                            onClick={() => {
                              const url = profile.social_media.facebook;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#1877F2', 0.05),
                              borderColor: alpha('#1877F2', 0.3),
                              color: '#1877F2',
                              '&:hover': { 
                                bgcolor: alpha('#1877F2', 0.1),
                                borderColor: '#1877F2',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Facebook
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.facebook.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.twitter && profile.social_media.twitter.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<TwitterIcon />}
                            onClick={() => {
                              const url = profile.social_media.twitter;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#1DA1F2', 0.05),
                              borderColor: alpha('#1DA1F2', 0.3),
                              color: '#1DA1F2',
                              '&:hover': { 
                                bgcolor: alpha('#1DA1F2', 0.1),
                                borderColor: '#1DA1F2',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Twitter
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.twitter.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.instagram && profile.social_media.instagram.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<InstagramIcon sx={{ color: '#E4405F' }} />}
                            onClick={() => {
                              const url = profile.social_media.instagram;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#E4405F', 0.05),
                              borderColor: alpha('#E4405F', 0.3),
                              color: '#E4405F',
                              '&:hover': { 
                                bgcolor: alpha('#E4405F', 0.1),
                                borderColor: '#E4405F',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Instagram
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.instagram.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                        {profile.social_media.linkedin && profile.social_media.linkedin.trim() && (
                          <Button
                            variant="outlined"
                            startIcon={<LinkedInIcon sx={{ color: '#0077B5' }} />}
                            onClick={() => {
                              const url = profile.social_media.linkedin;
                              const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
                                ? url 
                                : `https://${url}`;
                              window.open(formattedUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              py: 1,
                              bgcolor: alpha('#0077B5', 0.05),
                              borderColor: alpha('#0077B5', 0.3),
                              color: '#0077B5',
                              '&:hover': { 
                                bgcolor: alpha('#0077B5', 0.1),
                                borderColor: '#0077B5',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                LinkedIn
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                {profile.social_media.linkedin.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                              </Typography>
                            </Box>
                          </Button>
                        )}
                      </Box>
                      {isProfileOwner() && (
                        <Button
                          variant="text"
                          startIcon={<EditIcon />}
                          onClick={handleSocialMediaEdit}
                          size="small"
                          sx={{ 
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            textTransform: 'none'
                          }}
                        >
                          Edit Social Media
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Action Buttons for Owner */}
                  {isProfileOwner() && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                      <Button 
                        variant="contained" 
                        startIcon={<EditIcon />} 
                        onClick={handleEditProfile}
                        sx={{ borderRadius: 2 }}
                      >
                        Edit Profile
                      </Button>
                      <Button 
                        variant="outlined" 
                        startIcon={<AddPhotoIcon />} 
                        onClick={handleUploadClick}
                        sx={{ borderRadius: 2 }}
                      >
                        Add Photos
                      </Button>
                      <Button 
                        variant="outlined" 
                        startIcon={<EventIcon />} 
                        onClick={() => history.push('/events')}
                        sx={{ borderRadius: 2 }}
                      >
                        Find Events
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Stats Cards Sidebar */}
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  {/* Rating Card */}
                  <Card sx={{ 
                    borderRadius: 3, 
                    boxShadow: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Rating
                        </Typography>
                        <StarIcon sx={{ color: theme.palette.warning.main }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : 'N/A'}
                        </Typography>
                        {stats.averageRating > 0 && (
                          <Rating value={stats.averageRating} readOnly precision={0.1} size="small" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Average rating from reviews
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Applications Card */}
                  <Card sx={{ 
                    borderRadius: 3, 
                    boxShadow: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Applications
                        </Typography>
                        <StoreIcon color="primary" />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                        {stats.totalApplications}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total applications submitted
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Events Attended Card */}
                  <Card sx={{ 
                    borderRadius: 3, 
                    boxShadow: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Events Attended
                        </Typography>
                        <CheckCircleIcon color="success" />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                        {stats.attendedEvents}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successful participations
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Tabs Section */}
        <Paper 
          sx={{ 
            borderRadius: 3, 
            boxShadow: 4, 
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab label="About" />
              <Tab label="Gallery" />
              <Tab label="Events" />
              <Tab label="Reviews" />
            </Tabs>
          </Box>

          {/* About Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.3) }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    About
                  </Typography>
                  {isProfileOwner() && (
                    <IconButton onClick={handleAboutEdit} color="primary">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: 'text.primary' }}>
                  {profile?.bio || profile?.about || 'No description available. Click edit to add information about your business.'}
                </Typography>
              </Paper>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                {profile?.email && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                          }}
                        >
                          <EmailIcon color="success" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                {profile?.contact_info && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                          }}
                        >
                          <PhoneIcon color="info" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.contact_info}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                {profile?.location && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                          }}
                        >
                          <LocationIcon color="warning" />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {profile.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Gallery Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.3) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Gallery
                </Typography>
                {isProfileOwner() && (
                  <Button
                    variant="contained"
                    startIcon={<AddPhotoIcon />}
                    onClick={handleUploadClick}
                    sx={{ borderRadius: 2 }}
                  >
                    Upload Image
                  </Button>
                )}
              </Box>
              {profile?.gallery && profile.gallery.length > 0 ? (
                <ImageList cols={3} gap={12} sx={{ mt: 0 }}>
                  {profile.gallery.map((image, index) => {
                    const fullUrl = getImageUrl(image.image_url);
                    return (
                      <ImageListItem key={image.id || index}>
                        <img
                          src={fullUrl}
                          alt={image.caption || `Gallery image ${index + 1}`}
                          loading="lazy"
                          style={{ 
                            height: 280, 
                            objectFit: 'cover',
                            borderRadius: 8,
                            cursor: 'pointer',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                          onClick={() => {
                            setSelectedImageIndex(index);
                            setLightboxOpen(true);
                          }}
                        />
                        {(image.caption || isProfileOwner()) && (
                          <ImageListItemBar
                            title={image.caption}
                            subtitle={isProfileOwner() ? 'Click to delete' : undefined}
                            actionIcon={
                              isProfileOwner() && (
                                <IconButton
                                  sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteImage(image.id);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )
                            }
                            sx={{
                              borderBottomLeftRadius: 8,
                              borderBottomRightRadius: 8,
                            }}
                          />
                        )}
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AddPhotoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No images in gallery yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload some photos to showcase your work!
                  </Typography>
                  {isProfileOwner() && (
                    <Button
                      variant="contained"
                      startIcon={<AddPhotoIcon />}
                      onClick={handleUploadClick}
                      sx={{ borderRadius: 2 }}
                    >
                      Upload Your First Photo
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Events Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.3) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                Attended Events
              </Typography>
              {attendedEventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : attendedEvents?.length > 0 ? (
                <Grid container spacing={3}>
                  {attendedEvents.map((event) => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                      <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardMedia
                          component="img"
                          height="180"
                          image={event.image_url ? getImageUrl(event.image_url) : 'https://via.placeholder.com/400x180'}
                          alt={event.title}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom noWrap>
                            {event.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {event.location}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                              Successfully Participated
                            </Typography>
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => history.push(`/event/${event.id}`)}>
                            View Event
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No events attended yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Start applying to events to build your portfolio!
                  </Typography>
                  {isProfileOwner() && (
                    <Button
                      variant="contained"
                      startIcon={<EventIcon />}
                      onClick={() => history.push('/events')}
                      sx={{ borderRadius: 2 }}
                    >
                      Find Events
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Reviews Tab */}
          {activeTab === 3 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.3) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                Reviews
              </Typography>
              {profile?.reviews && profile.reviews.length > 0 ? (
                <Stack spacing={3}>
                  {profile.reviews.map((review, index) => (
                    <Card key={index} sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar src={review.user_avatar} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {review.user_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(review.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Rating value={review.rating} readOnly />
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                          {review.comment}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <StarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No reviews yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reviews will appear here once customers start rating your business.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Instagram Tab - Hidden */}
          {false && activeTab === 4 && (
            <Box sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 0.3) }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                Instagram
              </Typography>
              
              {!profile?.instagram_connected ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: 2,
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 64, color: '#E4405F', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Connect Your Instagram Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                    Connect your Instagram account to display your latest posts and showcase your work on your vendor profile.
                  </Typography>
                  {isProfileOwner() && (
                    <Button
                      variant="contained"
                      startIcon={<InstagramIcon />}
                      onClick={handleConnectInstagram}
                      sx={{
                        bgcolor: '#E4405F',
                        '&:hover': {
                          bgcolor: '#C13584',
                        },
                      }}
                    >
                      Connect Instagram
                    </Button>
                  )}
                </Paper>
              ) : (
                <Box>
                  {/* Instagram Profile Info */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {profile?.instagram_profile_picture ? (
                        <Avatar
                          src={profile.instagram_profile_picture}
                          sx={{ width: 64, height: 64 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 64, height: 64, bgcolor: '#E4405F' }}>
                          <InstagramIcon />
                        </Avatar>
                      )}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          @{profile?.instagram_username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Instagram Account Connected
                        </Typography>
                      </Box>
                      {isProfileOwner() && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleDisconnectInstagram}
                          disabled={disconnectInstagramMutation.isLoading}
                        >
                          Disconnect
                        </Button>
                      )}
                    </Box>
                  </Paper>

                  {/* Instagram Media */}
                  {isProfileOwner() ? (
                    <>
                      {instagramLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : instagramMedia.length > 0 ? (
                        <ImageList cols={3} gap={16} sx={{ mb: 2 }}>
                          {instagramMedia.map((post) => (
                            <ImageListItem
                              key={post.id}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                overflow: 'hidden',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  transition: 'transform 0.2s',
                                },
                              }}
                              onClick={() => {
                                if (post.permalink) {
                                  window.open(post.permalink, '_blank', 'noopener,noreferrer');
                                }
                              }}
                            >
                              <img
                                src={post.media_url || post.thumbnail_url}
                                alt={post.caption || 'Instagram post'}
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                              {post.caption && (
                                <ImageListItemBar
                                  title={post.caption.length > 50 ? `${post.caption.substring(0, 50)}...` : post.caption}
                                  subtitle={post.timestamp ? new Date(post.timestamp).toLocaleDateString() : ''}
                                  sx={{
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                                  }}
                                />
                              )}
                            </ImageListItem>
                          ))}
                        </ImageList>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            No Instagram posts found. Posts will appear here once you have media on your Instagram account.
                          </Typography>
                        </Paper>
                      )}
                    </>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        Instagram posts are only visible to the account owner.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* Dialogs */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Gallery Image</DialogTitle>
          <DialogContent>
            {selectedImage && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 8 }}
                />
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Image Caption"
              fullWidth
              variant="outlined"
              value={imageCaption}
              onChange={(e) => setImageCaption(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUploadConfirm}
              variant="contained"
              disabled={uploadProgress}
            >
              {uploadProgress ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={socialMediaDialogOpen} onClose={() => setSocialMediaDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Social Media Links</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="facebook-input">Facebook</InputLabel>
                <OutlinedInput
                  id="facebook-input"
                  value={socialMediaLinks.facebook}
                  onChange={handleSocialMediaChange('facebook')}
                  startAdornment={
                    <InputAdornment position="start">
                      <FacebookIcon />
                    </InputAdornment>
                  }
                  placeholder="https://facebook.com/yourpage"
                  label="Facebook"
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="twitter-input">Twitter</InputLabel>
                <OutlinedInput
                  id="twitter-input"
                  value={socialMediaLinks.twitter}
                  onChange={handleSocialMediaChange('twitter')}
                  startAdornment={
                    <InputAdornment position="start">
                      <TwitterIcon />
                    </InputAdornment>
                  }
                  placeholder="https://twitter.com/yourhandle"
                  label="Twitter"
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="instagram-input">Instagram</InputLabel>
                <OutlinedInput
                  id="instagram-input"
                  value={socialMediaLinks.instagram}
                  onChange={handleSocialMediaChange('instagram')}
                  startAdornment={
                    <InputAdornment position="start">
                      <InstagramIcon />
                    </InputAdornment>
                  }
                  placeholder="https://instagram.com/yourprofile"
                  label="Instagram"
                />
              </FormControl>

              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="linkedin-input">LinkedIn</InputLabel>
                <OutlinedInput
                  id="linkedin-input"
                  value={socialMediaLinks.linkedin}
                  onChange={handleSocialMediaChange('linkedin')}
                  startAdornment={
                    <InputAdornment position="start">
                      <LinkedInIcon />
                    </InputAdornment>
                  }
                  placeholder="https://linkedin.com/in/yourprofile"
                  label="LinkedIn"
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSocialMediaDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSocialMediaSave}
              variant="contained"
              disabled={updateSocialMediaMutation.isLoading}
            >
              {updateSocialMediaMutation.isLoading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit About Section</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="About"
              fullWidth
              multiline
              rows={8}
              variant="outlined"
              value={aboutText}
              onChange={(e) => setAboutText(e.target.value)}
              placeholder="Tell people about your business, services, and experience..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAboutDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAboutSave}
              variant="contained"
              disabled={updateAboutMutation.isLoading}
            >
              {updateAboutMutation.isLoading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={coverInputRef}
          onChange={handleCoverPhotoChange}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={profileInputRef}
          onChange={handleProfilePictureChange}
        />

        {/* Image Lightbox Dialog */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              maxHeight: '90vh',
              m: 2,
            },
          }}
        >
          {profile?.gallery && profile.gallery[selectedImageIndex] && (
            <>
              <DialogTitle
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'transparent',
                  color: 'white',
                  pb: 1,
                }}
              >
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {selectedImageIndex + 1} / {profile.gallery.length}
                </Typography>
                <IconButton
                  onClick={() => setLightboxOpen(false)}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'transparent' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    position: 'relative',
                  }}
                >
                  {/* Previous Button */}
                  {selectedImageIndex > 0 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex(selectedImageIndex - 1);
                      }}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        },
                        zIndex: 10,
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}

                  {/* Image */}
                  <img
                    src={getImageUrl(profile.gallery[selectedImageIndex].image_url)}
                    alt={profile.gallery[selectedImageIndex].caption || 'Gallery image'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: 8,
                    }}
                  />

                  {/* Next Button */}
                  {selectedImageIndex < profile.gallery.length - 1 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex(selectedImageIndex + 1);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        },
                        zIndex: 10,
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  )}
                </Box>

                {/* Caption */}
                {profile.gallery[selectedImageIndex].caption && (
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'white',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {profile.gallery[selectedImageIndex].caption}
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default VendorProfile;
